/* eslint-disable no-console */
/**
 * Algolia Conversion Tracking
 *
 * This file contains functions to track Algolia conversions through GTM.
 * It connects search data stored by the SearchInsights component with booking events.
 */

import aa from 'search-insights';

// Initialize Algolia Insights
const algoliaAppId = process.env.REACT_APP_ALGOLIA_APP_ID;
const algoliaApiKey = process.env.REACT_APP_ALGOLIA_SEARCH_KEY;
let isAlgoliaInitialized = false;

if (!algoliaAppId || !algoliaApiKey) {
  console.log('Algolia Insights credentials not found. Tracking will be disabled.');
}

if (algoliaAppId && algoliaApiKey) {
  try {
    aa('init', {
      appId: algoliaAppId,
      apiKey: algoliaApiKey,
      useCookie: true, // Optional: Helps with user identification across sessions/subdomains
    });
    isAlgoliaInitialized = true;
  } catch (initError) {
    console.log('Failed to initialize Algolia Insights:', initError);
  }
} else {
  console.log('Algolia Insights credentials not found. Tracking will be disabled.');
}

// Track Algolia conversion for a booking event
export function trackAlgoliaConversion(eventName, value, userId = '') {
  // Only proceed if initialization was successful
  if (!isAlgoliaInitialized) {
    console.log('Algolia Insights not initialized, skipping event tracking.');
    return;
  }

  try {
    // Get stored search data
    const searchDataStr = localStorage.getItem('algolia_search_data');
    console.log('DEBUG: searchDataStr', searchDataStr);
    if (!searchDataStr) {
      // eslint-disable-next-line no-console
      console.log('No search data found');
      return;
    }

    const searchData = JSON.parse(searchDataStr);

    // Don't count conversions after 36 hours
    const isRecent = new Date().getTime() - searchData.timestamp < 36 * 60 * 60 * 1000;

    if (!isRecent || !searchData.queryID) {
      // eslint-disable-next-line no-console
      console.log('Search data expired or invalid');
      return;
    }

    // Use userId from parameter, then from search data, or empty string as fallback
    const userToken = userId || searchData.userToken || '';
    // eslint-disable-next-line no-console
    console.log('DEBUG: userToken', userToken);

    // Log the conversion event for debugging
    // eslint-disable-next-line no-console
    console.log('Tracking Algolia conversion:', {
      eventName,
      queryID: searchData.queryID,
      objectIDs: searchData.objectIDs,
      value,
      userToken,
    });

    // Send conversion event to Algolia
    aa('convertedObjectIDsAfterSearch', {
      userToken,
      eventName,
      index: searchData.indexName,
      queryID: searchData.queryID,
      objectIDs: searchData.objectIDs,
      currency: 'USD',
      value: value || 0,
    });

    // Also push to dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'algoliaConversion',
      conversionValue: value,
      conversionType: eventName,
      algoliaQueryID: searchData.queryID,
    });

    // Clear stored data to prevent duplicate conversion tracking
    localStorage.removeItem('algolia_search_data');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Error tracking Algolia conversion:', e);
  }
}

// Track Algolia conversion directly without requiring search data
export function trackAlgoliaDirectConversion(
  eventName,
  value,
  objectID,
  userId = '',
  indexName = 'listings'
) {
  // Only proceed if initialization was successful
  if (!isAlgoliaInitialized) {
    console.log('Algolia Insights not initialized, skipping event tracking.');
    return;
  }

  try {
    // Use provided objectID and userId
    const userToken = userId || '';

    // Log the conversion event for debugging
    // eslint-disable-next-line no-console
    console.log('Tracking Algolia direct conversion:', {
      eventName,
      objectIDs: [objectID],
      value,
      userToken,
    });

    // Send conversion event to Algolia
    aa('convertedObjectIDs', {
      userToken,
      eventName,
      index: indexName,
      objectIDs: [objectID],
      currency: 'USD',
      value: value || 0,
    });

    // Also push to dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'algoliaConversion',
      conversionValue: value,
      conversionType: eventName,
      algoliaObjectID: objectID,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Error tracking Algolia direct conversion:', e);
  }
}

// Export conversion event names for consistency
export const CONVERSION_EVENTS = {
  BOOKING_REQUEST_SUBMITTED: 'Booking Request Submitted',
  BOOKING_REQUEST_ACCEPTED: 'Booking Request Accepted',
};
